import {fromObject, parseDate, today} from 'common/date';
import * as React from 'react';
import {
  FilterMenuShell,
  FilterPill,
  MenuRow,
} from 'src/components/FilterDropdown';
import Popover from 'src/Layer/Popover';
import {cn} from 'src/lib/utils';

export type DateFilterState = {
  startDate: string | null;
  endDate: string | null;
};

// How many years the menu lists by default (current year plus the
// twenty-four before it) when no earlier bound is supplied. The year column
// scrolls, so a long list costs no header height — and imported statement
// history goes back far further than a single decade.
const DEFAULT_YEAR_SPAN = 25;

// Fixed English month labels rather than luxon's locale-dependent formatting,
// so the menu (and screenshot baselines) render the same everywhere.
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// The calendar-year range for a given year, as ISO date strings. fromObject
// always yields a valid date here, so toISODate never returns null; the ??
// fallbacks only satisfy the type.
const yearRange = (year: number): DateFilterState => ({
  startDate: fromObject({year, month: 1, day: 1}).toISODate() ?? null,
  endDate: fromObject({year, month: 12, day: 31}).toISODate() ?? null,
});

// The calendar-month range for a given year and month, as ISO date strings.
// Same toISODate caveat as yearRange.
const monthRange = (year: number, month: number): DateFilterState => {
  const start = fromObject({year, month, day: 1});
  return {
    startDate: start.toISODate() ?? null,
    endDate: start.endOf('month').toISODate() ?? null,
  };
};

// The year whose exact calendar range equals this value, or null. Both bounds
// must be present and match Jan 1 / Dec 31 of the same year — a frontier-
// truncated or half-open range reads as a custom range instead.
const matchedYear = (value: DateFilterState): number | null => {
  if (!value.startDate || !value.endDate) return null;
  const year = parseDate(value.startDate).year;
  const range = yearRange(year);
  return range.startDate === value.startDate && range.endDate === value.endDate
    ? year
    : null;
};

type MonthSelection = {year: number; month: number};

// The month whose exact calendar range equals this value, or null — the
// month-granularity sibling of matchedYear. The two are disjoint by
// construction: no whole-year range is also a whole-month range.
const matchedMonth = (value: DateFilterState): MonthSelection | null => {
  if (!value.startDate || !value.endDate) return null;
  const start = parseDate(value.startDate);
  const range = monthRange(start.year, start.month);
  return range.startDate === value.startDate && range.endDate === value.endDate
    ? {year: start.year, month: start.month}
    : null;
};

const isEmpty = (value: DateFilterState): boolean =>
  !value.startDate && !value.endDate;

type Props = {
  value: DateFilterState;
  onChange: (value: DateFilterState) => void;
  /**
   * Earliest year offered in the year column. Defaults to twenty-four years
   * before the current year. A year older than this can still be reached
   * through the custom range inputs.
   */
  minYear?: number;
  /**
   * Let the pill contract and ellipsize rather than hold its natural width.
   * For a header that keeps this filter on the same row as its actions, where
   * "Dates: Custom range…" is wider than the space beside them.
   */
  truncateLabel?: boolean;
  /**
   * What an empty value means to this consumer. 'filter-off' (default):
   * no filter — the pill is inactive but the menu's All time row shows
   * selected, since that is the effective state of the list. 'all-time':
   * the empty range is an explicit choice — the pill reads "All time".
   * 'unset': the value belongs to some other control entirely (the
   * analytics relative presets) — nothing here renders as selected.
   */
  emptyMeaning?: 'filter-off' | 'all-time' | 'unset';
};

/**
 * Absolute date filter for reverse-chronological lists, as two panes in one
 * menu: a scrolling year column (All time and Custom range… at the top, then
 * one row per year, newest first, under decade headers) beside a month column
 * for the focused year (also newest first, mirroring the years) — so a year is
 * one click and a month two, with no typing. Clicking a year applies the whole-year filter and keeps the menu
 * open for optional month refinement; clicking it again confirms the year and
 * closes, as does picking a month. The custom inputs live in the menu's own
 * footer, so the control is always exactly one pill tall in the row that
 * hosts it. The external value is a `{startDate, endDate}` pair of ISO date
 * strings — a month is just first-of-month → last-of-month, so consumers are
 * untouched by the granularity.
 */
export const DateFilterSelect = ({
  value,
  onChange,
  minYear,
  truncateLabel = false,
  emptyMeaning = 'filter-off',
}: Props) => {
  const now = today();
  const currentYear = now.year;
  const currentMonth = now.month;
  const earliestYear = minYear ?? currentYear - (DEFAULT_YEAR_SPAN - 1);
  const years: number[] = [];
  for (let year = currentYear; year >= earliestYear; year--) {
    years.push(year);
  }

  const activeYear = matchedYear(value);
  const activeMonth = matchedMonth(value);
  // A non-empty value that isn't a whole calendar year or month is a custom
  // range.
  const isCustomValue =
    !isEmpty(value) && activeYear === null && activeMonth === null;

  // Custom mode is derived rather than stored, so it can never contradict the
  // controlled value: the inputs are what a custom range looks like, and a menu
  // the user opened and asked for them in is the only other reason to show
  // them. Storing it would go stale the moment the value is cleared from
  // outside — the Transactions header's Reset filters, say — leaving the pill
  // reading "Custom range…" over an unfiltered list, with the inputs hidden
  // inside the closed menu and no way to see why.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [customRequested, setCustomRequested] = React.useState(false);
  const showCustom = isCustomValue || (menuOpen && customRequested);

  // Which year's months the grid shows. Reset from the value on every open,
  // so the transient focus can't outlive the selection it followed.
  const [focusYear, setFocusYear] = React.useState(currentYear);

  const changeOpen = (open: boolean) => {
    if (open) {
      setFocusYear(activeMonth?.year ?? activeYear ?? currentYear);
    } else {
      setCustomRequested(false);
    }
    setMenuOpen(open);
  };

  // The footer sits after every year in DOM order, so a keyboard user who
  // picks "Custom range…" would otherwise tab through two decades of options
  // to reach the controls they just asked for. Move focus there instead —
  // only on the transition, so re-opening a menu that is already in custom
  // mode still lands on the option list.
  const startInputRef = React.useRef<HTMLInputElement | null>(null);
  const focusStartInput = React.useRef(false);
  React.useEffect(() => {
    if (focusStartInput.current && showCustom) {
      focusStartInput.current = false;
      startInputRef.current?.focus();
    }
  }, [showCustom, customRequested]);

  const selectAllTime = () => {
    onChange({startDate: null, endDate: null});
    changeOpen(false);
  };

  const selectCustom = () => {
    // Reveal the inputs without clearing, so a range already picked becomes
    // the starting point the user edits.
    focusStartInput.current = true;
    setCustomRequested(true);
  };

  const selectYear = (year: number) => {
    // Clicking the year whose whole-year filter is already applied reads as
    // confirmation: keep it and close, saving the dismissal click.
    if (activeYear === year && !showCustom) {
      changeOpen(false);
      return;
    }
    setCustomRequested(false);
    setFocusYear(year);
    onChange(yearRange(year));
  };

  const selectWholeYear = () => {
    onChange(yearRange(focusYear));
    changeOpen(false);
  };

  const selectMonth = (month: number) => {
    onChange(monthRange(focusYear, month));
    changeOpen(false);
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({...value, startDate: e.target.value || null});
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({...value, endDate: e.target.value || null});
  };

  // The checked year while a month is active is the month's year: the value
  // lives inside it, and the grid beside it shows which slice.
  const checkedYear = activeMonth?.year ?? activeYear;

  const yearColumn: React.ReactNode[] = [];
  let lastDecade: number | null = null;
  for (const year of years) {
    const decade = Math.floor(year / 10) * 10;
    if (decade !== lastDecade) {
      lastDecade = decade;
      yearColumn.push(
        <div
          key={`decade-${decade}`}
          className="text-muted-foreground px-3 pt-2 pb-1 text-xs font-semibold tracking-wider uppercase"
        >
          {decade}s
        </div>,
      );
    }
    yearColumn.push(
      <MenuRow
        key={year}
        selected={!showCustom && checkedYear === year}
        onClick={() => selectYear(year)}
        label={String(year)}
      />,
    );
  }

  const menu = (
    <FilterMenuShell
      footer={
        showCustom ? (
          // Wraps rather than squeezes: two native date controls want ~325px
          // together, more than a 320px phone leaves once the menu is capped
          // to the viewport, and squeezing them clips the picker indicator.
          // Below that width the end date drops to its own line instead.
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={startInputRef}
              type="date"
              value={value.startDate ?? ''}
              onChange={handleStartChange}
              className="border-border bg-background text-foreground min-w-32 flex-1 rounded border px-2 py-1 text-sm"
            />
            <span className="text-muted-foreground text-sm">to</span>
            <input
              type="date"
              value={value.endDate ?? ''}
              onChange={handleEndChange}
              className="border-border bg-background text-foreground min-w-32 flex-1 rounded border px-2 py-1 text-sm"
            />
          </div>
        ) : undefined
      }
    >
      <div className="flex min-h-0">
        <div className="border-border flex w-40 shrink-0 flex-col overflow-y-auto border-r py-1">
          <MenuRow
            selected={
              isEmpty(value) && !showCustom && emptyMeaning !== 'unset'
            }
            onClick={selectAllTime}
            label="All time"
          />
          {/* Custom sits directly under All time rather than after the years:
              with two decades listed it would otherwise be a scroll away. */}
          <MenuRow
            selected={showCustom}
            onClick={selectCustom}
            label="Custom range…"
          />
          {yearColumn}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-3 px-3 pt-2 pb-1">
            <span className="text-sm font-semibold tabular-nums">
              {focusYear}
            </span>
            <button
              type="button"
              role="menuitem"
              onClick={selectWholeYear}
              className={cn(
                'cursor-pointer rounded px-1.5 py-0.5 text-xs whitespace-nowrap',
                !showCustom && activeYear === focusYear
                  ? 'bg-accent text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
              )}
            >
              Whole year
            </button>
          </div>
          {/* One column, December first: the list mirrors the year column and
              the reverse-chronological lists this filters, so the most recent
              month is always at the top. The number rides each label
              ("5 - May") in a fixed slot, keeping the labels aligned. */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-1 pb-1">
            {MONTH_LABELS.map((_, index) => {
              const month = MONTH_LABELS.length - index;
              const label = MONTH_LABELS[month - 1];
              const disabled =
                focusYear === currentYear && month > currentMonth;
              const selected =
                !showCustom &&
                activeMonth != null &&
                activeMonth.year === focusYear &&
                activeMonth.month === month;
              return (
                <button
                  key={month}
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  onClick={() => selectMonth(month)}
                  className={cn(
                    'flex cursor-pointer items-center gap-1.5 rounded px-2 py-1 text-sm',
                    selected ? 'bg-accent font-medium' : 'hover:bg-accent/50',
                    'disabled:text-muted-foreground/50 disabled:cursor-default disabled:hover:bg-transparent',
                  )}
                >
                  <span className="w-5 shrink-0 text-right tabular-nums">
                    {month}
                  </span>
                  {/* Inherits the button's color (opacity, not a fixed color)
                      so disabled rows dim it along with the label. */}
                  <span className="opacity-60">-</span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </FilterMenuShell>
  );

  const selectionLabel = showCustom
    ? 'Custom range…'
    : activeMonth
      ? `${MONTH_LABELS[activeMonth.month - 1]} ${activeMonth.year}`
      : activeYear !== null
        ? String(activeYear)
        : emptyMeaning === 'all-time' && isEmpty(value)
          ? 'All time'
          : null;

  // Self-contained flex wrapper so the component lays out the same whether a
  // consumer drops it into a flex filter row (the account Transactions tab) or
  // a plain block header slot (the review page), where a bare block-level
  // button would otherwise stretch to full width.
  return (
    <div
      className={cn(
        'flex items-center',
        truncateLabel ? 'min-w-0' : 'shrink-0',
      )}
    >
      <Popover
        open={menuOpen}
        popoverContents={menu}
        popoverAnchor="bottom-left"
        onOutsideClick={() => changeOpen(false)}
      >
        {(anchorRef: React.RefCallback<HTMLButtonElement>) => (
          <FilterPill
            anchorRef={anchorRef}
            onClick={() => changeOpen(!menuOpen)}
            label="Dates"
            value={selectionLabel}
            active={selectionLabel != null}
            truncateLabel={truncateLabel}
          />
        )}
      </Popover>
    </div>
  );
};
